import { useCallback, useEffect, useState } from "react";
import { Search, SearchX, Store } from "lucide-react";
import { useDolfinClient } from "@/components/DolfinProvider";
import { formatCurrency, formatDate } from "@/lib/format";
import { PageContainer, PageHeader } from "@/components/PagePrimitives";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import {
  EmptyState,
  ErrorState,
  HeaderRow,
  LoadingState,
  MerchantCell,
  Pager,
  TH,
  TableShell,
  prettyCategory,
  rowLink,
} from "@/components/ExpenseUi";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { cn } from "@/lib/utils";
import type { components } from "@dolfin/api-public";

type MerchantResponse = components["schemas"]["MerchantResponse"];
type MerchantSpendResponse = components["schemas"]["MerchantSpendResponse"];

const PAGE_SIZE = 20;

// Spend in each currency, largest first: "£43.50 + €10.00". Never summed across currencies.
export function formatSpend(spend?: MerchantSpendResponse[] | null) {
  if (!spend?.length) return "—";
  return spend.map((s) => formatCurrency(s.amount, s.currency)).join(" + ");
}

export function MerchantsPage({ onSelect }: { onSelect?: (merchantId: string) => void }) {
  const api = useDolfinClient();
  const [merchants, setMerchants] = useState<MerchantResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const q = useDebouncedValue(search, 300);
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);

  const fetchPage = useCallback(
    async (cursor?: string) => {
      setLoading(true);
      setError(null);
      const query: Record<string, string | number> = { Limit: PAGE_SIZE };
      if (q.trim()) query.Q = q.trim();
      if (cursor) query.Cursor = cursor;
      const { data, error: apiError } = await api.GET("/v1/merchants", { params: { query } });
      if (apiError || !data) {
        setError("Failed to load merchants");
      } else {
        setMerchants(data.data ?? []);
        setNextCursor(data.meta?.cursors?.next ?? null);
      }
      setLoading(false);
    },
    [api, q],
  );

  useEffect(() => {
    setCursorStack([]);
    fetchPage();
  }, [fetchPage]);

  return (
    <PageContainer>
      <PageHeader
        eyebrow="Expenses"
        title="Merchants"
        subtitle="Where your card spend goes. Each merchant groups every way the card networks spell it, so SUMUP *BLUE ANCHOR and ZETTLE_*BLUEANC are one place."
      />

      <div className="relative mb-4 max-w-sm">
        <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          className="pl-9"
          placeholder="Search by name or spelling"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {loading ? (
        <LoadingState label="Loading merchants..." />
      ) : error ? (
        <ErrorState message={error} onRetry={() => fetchPage()} />
      ) : merchants.length === 0 ? (
        q ? (
          <EmptyState icon={SearchX} title="No merchants match">
            Nothing is called or spelled "{q}".
          </EmptyState>
        ) : (
          <EmptyState icon={Store} title="No merchants yet">
            Merchants appear here as your cards are used.
          </EmptyState>
        )
      ) : (
        <>
          <TableShell>
            <Table>
              <TableHeader>
                <HeaderRow>
                  <TableHead className={TH}>Merchant</TableHead>
                  <TableHead className={cn(TH, "text-right")}>Expenses</TableHead>
                  <TableHead className={TH}>Spellings</TableHead>
                  <TableHead className={TH}>Last used</TableHead>
                  <TableHead className={cn(TH, "text-right")}>Spend</TableHead>
                </HeaderRow>
              </TableHeader>
              <TableBody>
                {merchants.map((m) => (
                  <TableRow key={m.id} {...rowLink(onSelect ? () => onSelect(m.id!) : undefined)}>
                    <TableCell className="max-w-[320px]">
                      <MerchantCell
                        name={m.displayName}
                        secondary={prettyCategory(m.categoryLabel) ?? "Uncategorised"}
                      />
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{m.expenseCount}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {Number(m.aliasCount) > 1 ? `${m.aliasCount} spellings` : "1 spelling"}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                      {m.lastSeenAt ? formatDate(m.lastSeenAt) : "—"}
                    </TableCell>
                    <TableCell className="text-right font-medium tabular-nums">{formatSpend(m.spend)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableShell>
          <Pager
            page={cursorStack.length + 1}
            hasPrevious={cursorStack.length > 0}
            hasNext={!!nextCursor}
            onPrevious={() => {
              const stack = [...cursorStack];
              stack.pop();
              setCursorStack(stack);
              fetchPage(stack[stack.length - 1]);
            }}
            onNext={() => {
              if (!nextCursor) return;
              setCursorStack((prev) => [...prev, nextCursor]);
              fetchPage(nextCursor);
            }}
          />
        </>
      )}
    </PageContainer>
  );
}
